import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"
import LiquidIndicator from "../LiquidIndicator"

const Tabs = TabsPrimitive.Root

/**
 * Tab strip whose active pill GLIDES between tabs.
 *
 * The travel itself lives in SlidingIndicator, shared with the sidebar, so a
 * tab switch and a nav switch move with exactly the same curve.
 */
const TabsList = React.forwardRef(({ className, children, ...props }, ref) => {
  const inner = React.useRef(null)
  React.useImperativeHandle(ref, () => inner.current)
  return (
    <TabsPrimitive.List
      ref={inner}
      className={cn(
        "relative inline-flex h-11 items-center justify-center rounded-2xl border border-white/80 bg-white/62 backdrop-blur-md p-1.5 text-[#64748B] shadow-soft",
        className
      )}
      {...props}>
      <LiquidIndicator activeSelector='[role="tab"][data-state="active"]' />
      {children}
    </TabsPrimitive.List>
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // No background of its own — the gliding pill is the background. Only the
      // label colour crosses over, timed to meet the pill mid-travel.
      "relative z-10 inline-flex items-center justify-center whitespace-nowrap rounded-xl px-4 py-1.5",
      // Faster than the pill on purpose: the label turning white is the
      // click being acknowledged, and that must not wait ~800ms for the
      // pill to arrive.
      "text-sm font-semibold transition-colors duration-200 ease-smooth",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sky/60",
      "disabled:pointer-events-none disabled:opacity-50",
      "hover:text-brand-primary data-[state=active]:text-white data-[state=active]:hover:text-white",
      className
    )}
    {...props} />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

/**
 * Panel content. Radix unmounts the outgoing panel, so there is nothing to
 * cross-fade against — what reads as smooth here is the incoming panel
 * settling in while the pill is still travelling.
 */
const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "tabs-panel-enter mt-2 ring-offset-background focus-visible:outline-none",
      "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
